import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import CommandPalette from './CommandPalette';
import AICopilot from './AICopilot';
import NotificationCenter from './NotificationCenter';
import axios from 'axios';
import {
  LayoutDashboard, Sun, Moon, LogOut, ChefHat, Bell, Menu, X,
  UtensilsCrossed, ShoppingCart, Package, Tag, QrCode, Truck,
  Calculator, FileText, BarChart3, Lightbulb, Target,
  Brain, MessageCircle, Wand2, Megaphone, Gauge, Star, Mail, Banknote,
  Building2, Crown, Users, Activity, Shield,
  Monitor, ChevronLeft, ChevronRight, PanelLeftClose, PanelLeftOpen,
  Search, User, Sparkles, BookOpen
} from 'lucide-react';

const NAV_SECTIONS = [
  {
    key: 'dashboard',
    labelKey: 'modules.commandCenter',
    icon: LayoutDashboard,
    path: '/dashboard',
    single: true,
  },
  {
    key: 'operations',
    labelKey: 'modules.operations',
    icon: UtensilsCrossed,
    items: [
      { path: '/dashboard/operations/menu', labelKey: 'nav.menu', icon: UtensilsCrossed, permission: 'menu.view' },
      { path: '/dashboard/operations/magic-menu', labelKey: 'nav.magicMenu', icon: Sparkles, permission: 'menu.view' },
      { path: '/dashboard/operations/orders', labelKey: 'nav.orders', icon: ShoppingCart, permission: 'orders.view' },
      { path: '/dashboard/operations/inventory', labelKey: 'nav.inventory', icon: Package, permission: 'menu.view' },
      { path: '/dashboard/operations/offers', labelKey: 'nav.offers', icon: Tag, permission: 'menu.view' },
      { path: '/dashboard/operations/tables', labelKey: 'nav.tables', icon: QrCode, permission: 'tables.view' },
      { path: '/dashboard/operations/fleet', labelKey: 'nav.fleet', icon: Truck, permission: 'fleet.view' },
      { path: '/dashboard/operations/qr-menu', labelKey: 'nav.qrMenu', icon: QrCode, permission: 'menu.view' },
    ],
  },
  {
    key: 'finance',
    labelKey: 'modules.finance',
    icon: Calculator,
    items: [
      { path: '/dashboard/finance/flash-report', labelKey: 'nav.flashReport', icon: FileText, permission: 'accounting.dashboard' },
      { group: 'nav.coreAccounting' },
      { path: '/dashboard/finance/accounting', labelKey: 'nav.accounting', icon: Calculator, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/ledger', labelKey: 'nav.ledger', icon: BookOpen, permission: 'accounting.ledger.view' },
      { path: '/dashboard/finance/cash-waste', labelKey: 'nav.cashWaste', icon: Banknote, permission: 'accounting.dashboard' },
      { group: 'nav.planningGroup' },
      { path: '/dashboard/finance/planning', labelKey: 'nav.planning', icon: Target, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/vendors', labelKey: 'nav.vendors', icon: Users, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/invoices', labelKey: 'nav.invoices', icon: FileText, permission: 'invoices.view' },
      { group: 'nav.analyticsGroup' },
      { path: '/dashboard/finance/order-analytics', labelKey: 'nav.orderAnalytics', icon: BarChart3, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/smart-suggestions', labelKey: 'nav.smartSuggestions', icon: Lightbulb, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/reports', labelKey: 'nav.reports', icon: BarChart3, permission: 'accounting.dashboard' },
      { group: 'nav.aiGroup' },
      { path: '/dashboard/finance/ai-advisor', labelKey: 'nav.aiAdvisor', icon: Brain, permission: 'accounting.dashboard' },
    ],
  },
  {
    key: 'ai',
    labelKey: 'modules.ai',
    icon: Brain,
    items: [
      { path: '/dashboard/ai/conversational', labelKey: 'nav.conversational', icon: MessageCircle, permission: 'ai.tools' },
      { path: '/dashboard/ai/campaigns', labelKey: 'nav.campaigns', icon: Megaphone, permission: 'ai.tools' },
      { path: '/dashboard/ai/builder', labelKey: 'nav.builder', icon: Wand2, permission: 'ai.tools' },
      { path: '/dashboard/ai/agents', labelKey: 'nav.agents', icon: Brain, permission: 'ai.tools' },
      { path: '/dashboard/ai/usage', labelKey: 'nav.aiUsage', icon: Gauge, permission: 'ai.tools' },
      { path: '/dashboard/ai/reviews', labelKey: 'nav.reviews', icon: Star, permission: 'ai.tools' },
    ],
  },
  {
    key: 'settings',
    labelKey: 'modules.settings',
    icon: Building2,
    items: [
      { path: '/dashboard/settings/branches', labelKey: 'nav.branches', icon: Building2, permission: 'branches.view' },
      { path: '/dashboard/settings/team', labelKey: 'nav.team', icon: Users, permission: 'team.manage' },
      { path: '/dashboard/settings/subscription', labelKey: 'nav.subscription', icon: Crown, permission: 'subscription.manage' },
      { path: '/dashboard/settings/activity-log', labelKey: 'nav.activityLog', icon: Activity, permission: 'team.manage' },
      { path: '/dashboard/settings/my-data', labelKey: 'nav.myData', icon: Shield },
      { path: '/dashboard/settings/email-preferences', labelKey: 'nav.emailPrefs', icon: Mail },
    ],
  },
];

const QUICK_ACTIONS = [
  { path: '/dashboard/pos', labelKey: 'modules.pos', icon: Monitor, permission: 'pos.access', color: 'text-emerald-500' },
  { path: '/dashboard/kitchen', labelKey: 'modules.preparation', icon: ChefHat, permission: 'kitchen.access', color: 'text-orange-500' },
];


function DemoBanner({ userName }) {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('zouqai_demo_banner_hidden') === 'true');
  const [showRegister, setShowRegister] = useState(false);
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [emailConsent, setEmailConsent] = useState(true);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const { token, API } = useAuth();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const l = (ar, en) => isAr ? ar : en;

  if (dismissed && !showRegister) return null;

  const handleConvert = async () => {
    if (!regEmail || !regPassword) return;
    setRegLoading(true);
    setRegError('');
    try {
      await axios.post(`${API}/auth/convert-demo`, { email: regEmail, password: regPassword, name: userName, email_consent: emailConsent }, { headers: { Authorization: `Bearer ${token}` } });
      sessionStorage.setItem('zouqai_demo_banner_hidden', 'true');
      setShowRegister(false);
      setDismissed(true);
      window.location.reload();
    } catch (e) {
      setRegError(e.response?.data?.detail || l('حدث خطأ', 'Error'));
    } finally {
      setRegLoading(false);
    }
  };

  if (showRegister) {
    return (
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-amber-500/20 px-4 py-3" data-testid="demo-register-form">
        <div className="max-w-xl mx-auto flex flex-col sm:flex-row items-center gap-2">
          <span className="text-xs text-amber-200/80 whitespace-nowrap">{l('سجّل واحتفظ بكل بياناتك:', 'Register and keep all your data:')}</span>
          <input value={regEmail} onChange={e => setRegEmail(e.target.value)} type="email" placeholder={l('الإيميل', 'Email')} className="h-8 px-3 rounded-lg text-xs bg-black/20 border border-white/10 text-white placeholder:text-white/30 flex-1 min-w-0" />
          <input value={regPassword} onChange={e => setRegPassword(e.target.value)} type="password" placeholder={l('كلمة المرور', 'Password')} className="h-8 px-3 rounded-lg text-xs bg-black/20 border border-white/10 text-white placeholder:text-white/30 w-32" />
          <button onClick={handleConvert} disabled={regLoading} className="h-8 px-4 rounded-lg text-xs font-bold bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-50 whitespace-nowrap">
            {regLoading ? '...' : l('تأكيد', 'Confirm')}
          </button>
          <button onClick={() => setShowRegister(false)} className="text-white/40 hover:text-white/70 text-xs">{l('إلغاء', 'Cancel')}</button>
        </div>
        <div className="max-w-xl mx-auto mt-1.5 flex items-center gap-2 justify-center">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={emailConsent} onChange={e => setEmailConsent(e.target.checked)} className="rounded border-amber-500/30 bg-black/20" data-testid="email-consent-checkbox" />
            <span className="text-[10px] text-amber-200/60">{l('أوافق على استلام إشعارات وتقارير عبر البريد', 'I agree to receive notifications and reports via email')}</span>
          </label>
        </div>
        {regError && <p className="text-red-400 text-[11px] text-center mt-1">{regError}</p>}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-amber-500/20 px-4 py-2 flex items-center justify-between" data-testid="demo-banner">
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-xs text-amber-200/80">{l(`هذا عرض تجريبي لـ "${userName}"`, `Demo for "${userName}"`)}</span>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => setShowRegister(true)} data-testid="demo-register-btn"
          className="px-3 py-1 rounded-lg text-[11px] font-bold bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors">
          {l('سجّل واحتفظ بالبيانات', 'Register & keep data')}
        </button>
        <button onClick={() => { setDismissed(true); sessionStorage.setItem('zouqai_demo_banner_hidden', 'true'); }}
          className="text-white/30 hover:text-white/60 text-sm">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}


export default function AppLayout() {
  const { user, logout, activeBranch, setActiveBranch, branches, isDemo, token, businessType, hasPermission } = useAuth();
  const { t } = useTranslation();
  const { lang, toggleLanguage, dir } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('zouqai_sidebar_collapsed');
    return saved === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});

  const toggleTheme = () => { const d = document.documentElement.classList.toggle('dark'); setDark(d); localStorage.setItem('zouqai_theme', d ? 'dark' : 'light'); };
  const handleLogout = () => { logout(); navigate('/'); };
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('zouqai_sidebar_collapsed', String(next));
  };

  // Auto-open section based on current path
  useEffect(() => {
    for (const section of NAV_SECTIONS) {
      if (section.items?.some(item => location.pathname === item.path)) {
        setOpenSections(prev => ({ ...prev, [section.key]: true }));
        break;
      }
    }
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleSection = (key) => {
    if (collapsed) {
      setCollapsed(false);
      localStorage.setItem('zouqai_sidebar_collapsed', 'false');
      setOpenSections(prev => ({ ...prev, [key]: true }));
    } else {
      setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
    }
  };

  const isItemVisible = (item) => {
    if (item.group) return true;
    const bizOk = !item.visibleFor || item.visibleFor.includes(businessType);
    const permOk = !item.permission || hasPermission(item.permission);
    return bizOk && permOk;
  };

  const isActive = (path) => location.pathname === path;
  const isSectionActive = (section) => {
    if (section.single) return location.pathname === section.path;
    return section.items?.some(item => !item.group && location.pathname === item.path);
  };

  const renderSidebar = (isMobile = false) => (
    <div className={`flex flex-col h-full ${isMobile ? 'w-64' : ''}`}>
      {/* Logo */}
      <div className={`flex items-center gap-3 p-4 ${collapsed && !isMobile ? 'justify-center px-3' : ''}`}>
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20 flex-shrink-0">
          <ChefHat className="w-5 h-5 text-white" />
        </div>
        {(!collapsed || isMobile) && (
          <div className="min-w-0">
            <h1 className="font-bold text-base text-foreground leading-none" style={{ fontFamily: 'Cairo, sans-serif' }}>Zouq AI</h1>
            <p className="text-[10px] text-muted-foreground truncate">{activeBranch?.name || ''}</p>
          </div>
        )}
      </div>

      {/* Nav Sections */}
      <nav className="flex-1 overflow-y-auto px-2 pb-2 space-y-1" data-testid="app-sidebar-nav">
        {NAV_SECTIONS.map(section => {
          const Icon = section.icon;
          const sectionActive = isSectionActive(section);
          const isOpen = openSections[section.key];
          const visibleItems = section.items?.filter(isItemVisible) || [];

          if (section.single) {
            return (
              <Link key={section.key} to={section.path}
                data-testid={`nav-${section.key}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                  ${sectionActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'}
                  ${collapsed && !isMobile ? 'justify-center px-2' : ''}`}>
                <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${sectionActive ? 'text-primary' : ''}`} />
                {(!collapsed || isMobile) && <span>{t(section.labelKey)}</span>}
              </Link>
            );
          }

          if (visibleItems.length === 0) return null;

          return (
            <div key={section.key}>
              <button
                onClick={() => toggleSection(section.key)}
                data-testid={`nav-section-${section.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                  ${sectionActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'}
                  ${collapsed && !isMobile ? 'justify-center px-2' : ''}`}>
                <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${sectionActive ? 'text-primary' : ''}`} />
                {(!collapsed || isMobile) && (
                  <>
                    <span className="flex-1 text-start">{t(section.labelKey)}</span>
                    <ChevronLeft className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isOpen ? '-rotate-90' : dir === 'ltr' ? 'rotate-180' : ''}`} />
                  </>
                )}
              </button>

              {(isOpen || (collapsed && !isMobile)) && (!collapsed || isMobile) && (
                <div className="mt-0.5 ms-3 border-s border-border/40 ps-2 space-y-0.5">
                  {visibleItems.map((item, idx) => {
                    if (item.group) {
                      return (
                        <div key={`group-${idx}`} className="pt-2.5 pb-1 px-3">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{t(item.group)}</span>
                        </div>
                      );
                    }
                    const ItemIcon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <Link key={item.path} to={item.path}
                        onClick={() => isMobile && setMobileOpen(false)}
                        data-testid={`nav-item-${item.path.split('/').pop()}`}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-all
                          ${active ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                        <ItemIcon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-primary' : ''}`} />
                        <span className="truncate">{t(item.labelKey)}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Quick Actions (POS / Kitchen) */}
      <div className={`px-2 pb-2 space-y-1 border-t border-border/40 pt-2 ${collapsed && !isMobile ? 'px-1.5' : ''}`}>
        {QUICK_ACTIONS.filter(a => !a.permission || hasPermission(a.permission)).map(action => {
          const Icon = action.icon;
          return (
            <Link key={action.path} to={action.path}
              data-testid={`quick-${action.path.split('/').pop()}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                text-muted-foreground hover:text-foreground hover:bg-muted/60
                ${collapsed && !isMobile ? 'justify-center px-2' : ''}`}>
              <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${action.color}`} />
              {(!collapsed || isMobile) && <span>{t(action.labelKey)}</span>}
            </Link>
          );
        })}

        {/* Collapse toggle (desktop only) */}
        {!isMobile && (
          <button onClick={toggleCollapsed}
            data-testid="sidebar-collapse-toggle"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all justify-center">
            {collapsed
              ? <PanelLeftOpen className="w-4 h-4" />
              : <PanelLeftClose className="w-4 h-4" />
            }
          </button>
        )}
      </div>
    </div>
  );

  // Fullscreen modules (POS) render without sidebar/header
  if (location.pathname === '/dashboard/pos') {
    return (
      <div className="min-h-screen bg-background" dir={dir} data-testid="fullscreen-module">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" dir={dir}>
      {/* Top Header */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur-md h-14" data-testid="app-header">
        <div className="flex items-center h-full px-4 gap-3">
          {/* Mobile menu */}
          <Button variant="ghost" size="sm" className="lg:hidden h-8 w-8 p-0" onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </Button>

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <ChefHat className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-sm" style={{ fontFamily: 'Cairo, sans-serif' }}>Zouq AI</span>
          </div>

          <div className="flex-1" />

          <CommandPalette />

          {/* Branch Selector */}
          {branches.length > 0 && (
            <Select value={activeBranch?.id || ''} onValueChange={(val) => { const b = branches.find(br => br.id === val); if (b) setActiveBranch(b); }}>
              <SelectTrigger className="w-32 sm:w-44 h-8 text-xs" data-testid="branch-selector">
                <SelectValue placeholder={t('common.selectBranch')} />
              </SelectTrigger>
              <SelectContent>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          <NotificationCenter />

          <Button variant="ghost" size="sm" onClick={toggleLanguage} className="h-8 px-2 text-xs font-semibold" data-testid="lang-toggle">
            {lang === 'ar' ? 'EN' : 'عربي'}
          </Button>
          <Button variant="ghost" size="sm" onClick={toggleTheme} className="h-8 w-8 p-0" aria-label="Toggle theme">
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="h-8 gap-1 text-muted-foreground hover:text-red-500" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:block text-xs">{t('common.logout')}</span>
          </Button>
        </div>

        {/* Demo Banner */}
        {isDemo && (
          <div className="bg-gradient-to-l from-amber-500 to-orange-500 text-white px-4 py-1.5 flex items-center justify-between text-xs" data-testid="demo-banner">
            <span className="font-semibold">{t('demo.banner')}</span>
            <button onClick={() => { logout(); navigate('/login'); }}
              className="px-3 py-0.5 bg-white/20 hover:bg-white/30 rounded-full font-bold transition-colors"
              data-testid="demo-register-btn">
              {t('demo.registerNow')}
            </button>
          </div>
        )}
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <aside className={`hidden lg:flex flex-col shrink-0 border-border/50 bg-muted/20 overflow-hidden transition-all duration-300
          ${dir === 'rtl' ? 'border-l' : 'border-r'}
          ${collapsed ? 'w-16' : 'w-56'}`}
          data-testid="app-sidebar">
          {renderSidebar(false)}
        </aside>

        {/* Mobile Sidebar Overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMobileOpen(false)}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <aside className={`absolute top-0 bottom-0 w-64 bg-background shadow-2xl overflow-y-auto
              ${dir === 'rtl' ? 'right-0' : 'left-0'}`}
              onClick={e => e.stopPropagation()}
              data-testid="mobile-sidebar">
              {renderSidebar(true)}
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto" data-testid="app-main-content">
          {isDemo && <DemoBanner userName={user?.name} />}
          <Outlet />
        </main>
        <AICopilot />
      </div>
    </div>
  );
}
